import { useEffect } from 'react'
import { useTransitionState } from 'react-transition-state'

type TransitionStatus =
  | 'preEnter'
  | 'entering'
  | 'entered'
  | 'preExit'
  | 'exiting'
  | 'exited'
  | 'unmounted'

const ENTER_SUFFIX: Partial<Record<TransitionStatus, string>> = {
  preEnter: 'enter',
  entering: 'enter-active',
  entered: 'enter-done',
}

const APPEAR_SUFFIX: Partial<Record<TransitionStatus, string>> = {
  preEnter: 'appear',
  entering: 'appear-active',
  entered: 'appear-done',
}

const EXIT_SUFFIX: Partial<Record<TransitionStatus, string>> = {
  preExit: 'exit',
  exiting: 'exit-active',
  exited: 'exit-done',
}

/**
 * Maps react-transition-state statuses to the legacy
 * react-transition-group CSS class names so existing
 * stylesheets keep working without changes.
 *
 * When base is non-empty: "transition" + "enter" → "transition-enter"
 * When base is empty: just "enter"
 */
export function mapStatusToClassNames(
  base: string,
  status: TransitionStatus,
  appear: boolean,
): string {
  const prefix = base ? `${base}-` : ''
  const classes: string[] = []

  const enterPart = ENTER_SUFFIX[status]
  if (enterPart) {
    classes.push(`${prefix}${enterPart}`)
    if (appear) {
      classes.push(`${prefix}${APPEAR_SUFFIX[status]}`)
    }
  }

  const exitPart = EXIT_SUFFIX[status]
  if (exitPart) {
    classes.push(`${prefix}${exitPart}`)
  }

  return classes.join(' ')
}

interface UseCssTransitionOptions {
  in: boolean
  appear?: boolean
  mountOnEnter?: boolean
  unmountOnExit?: boolean
  timeout: number | { enter?: number; exit?: number }
  classNames?: string
}

/**
 * Drop-in replacement for react-transition-group's CSSTransition,
 * backed by react-transition-state. Emits the same CSS class names
 * (enter, enter-active, enter-done, exit, exit-active, exit-done,
 *  appear, appear-active, appear-done) so existing styles stay intact.
 */
export function useCssTransition({
  in: inProp,
  appear = false,
  mountOnEnter = false,
  unmountOnExit = false,
  timeout,
  classNames = '',
}: UseCssTransitionOptions) {
  const initialEntered = !appear && inProp

  // react-transition-group's CSSTransition starts unmounted when `in` is
  // false and either `unmountOnExit` or `mountOnEnter` is set. In
  // react-transition-state the initial unmount is controlled solely by
  // `mountOnEnter`, so fold `unmountOnExit` into it for the initial render
  // (only while `in` is false, to keep `appear` enter transitions mounted).
  const initialMountOnEnter = mountOnEnter || (unmountOnExit && !inProp)

  const [{ status, isMounted }, toggle] = useTransitionState({
    initialEntered,
    preEnter: true,
    preExit: true,
    mountOnEnter: initialMountOnEnter,
    unmountOnExit,
    timeout,
  })

  useEffect(() => {
    toggle(inProp)
  }, [inProp, toggle])

  const className = mapStatusToClassNames(classNames, status, appear)

  return { status, isMounted, className }
}
